<!-- UserRegistrationForm.vue -->
<script setup>
import { ref, computed, watch, nextTick, onMounted, onUnmounted } from "vue";
import { useRouter } from "vue-router";
import { useUserStore } from "@/stores/useUserStore";
import * as groupService from "@/services/groupService";
import * as userService from "@/services/userService";

// [PATCH] UsageTypeSelector removido — tipo de uso fixado em "indiferente"
import { useOnboardingStore } from "@/stores/useOnboardingStore";

// ── PATCH: LegalDocsModal para os links de Termos de Uso e Política de Privacidade ──
import LegalDocsModal from "@/components/Legal/LegalDocsModal.vue";
// ── FIM PATCH ────────────────────────────────────────────────────────────────────────

// ── PATCH: modal de boas-vindas pós-verificação no plano Free ────────────────────────
import WelcomeFreePlanModal from "@/components/User/WelcomeFreePlanModal.vue";
import WelcomeMobileModal from "@/components/User/WelcomeMobileModal.vue";

import { trackCompleteRegistration } from "@/utils/tracking";
// ── FIM PATCH ────────────────────────────────────────────────────────────────────────

// --- TOAST LÓGICA ---
const showToast = ref(false);
const toastMessage = ref("");
const toastTimeoutId = ref(null);
const toastType = ref("error"); // "error" | "success"

function showErrorToast(message) {
  toastType.value = "error";
  toastMessage.value = message;
  showToast.value = true;
  resetToastTimeout();
}

function showSuccessToast(message) {
  toastType.value = "success";
  toastMessage.value = message;
  showToast.value = true;
  resetToastTimeout();
}

function resetToastTimeout() {
  if (toastTimeoutId.value) clearTimeout(toastTimeoutId.value);
  toastTimeoutId.value = setTimeout(() => {
    showToast.value = false;
  }, 3000);
}
// --- FIM LÓGICA TOAST ---

// ── PATCH: estado do LegalDocsModal ──────────────────────────────────────────
const showLegalModal = ref(false);
const legalModalDefaultDoc = ref("terms");

function openLegalModal(docId) {
  legalModalDefaultDoc.value = docId;
  showLegalModal.value = true;
}

function closeLegalModal() {
  showLegalModal.value = false;
}
// ── FIM PATCH ─────────────────────────────────────────────────────────────────

// --- Lógica de Estado e Navegação ---
const registrationStep = ref(0);
const router = useRouter();
const userStore = useUserStore();
const onboardingStore = useOnboardingStore();

const props = defineProps({
  isSchoolSignup: { type: Boolean, default: false },
});

// login-with / forgot-password: e-mail já tem dono; o LoginForm fecha este
// modal e leva a pessoa ao login (com o e-mail preenchido) ou ao "Esqueci
// minha senha".
const emit = defineEmits(["close", "verified", "login-with", "forgot-password"]);
const isLoading = ref(false);
const institutionName = ref("");
const trialPlanGroupId = ref(null);

// ── PATCH: controla o modal de boas-vindas exibido após verificação da conta ──
const showWelcomeFreePlanModal = ref(false);
const showWelcomeMobileModal = ref(false);

const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
  navigator.userAgent
);
// ── FIM PATCH ───────────────────────────────────────────────────────────────

// [PATCH] pendingUsageType removido — relationship_type será "indiferente" por padrão

const user = ref({
  userName: "",
  fullName: "",
  birthDate: "",
  bio: "",
  country: "",
  profession: "",
  email: "",
  password: "",
  confirmPassword: "",
  agreedToTerms: false,
  phoneE164: "",
  phoneIsWhatsapp: false,
  isWhatsappPublic: false,
  telegramUserName: "",
  instagramUserName: "",
  linkedInUserName: "",
  facebookUserName: "",
});

const groupIds = computed(() =>
  trialPlanGroupId.value ? [trialPlanGroupId.value] : []
);

// Países para DDI
const countries = ref([
  { code: "355", name: "Albânia" },
  { code: "49", name: "Alemanha" },
  { code: "376", name: "Andorra" },
  { code: "54", name: "Argentina" },
  { code: "374", name: "Armênia" },
  { code: "43", name: "Áustria" },
  { code: "994", name: "Azerbaijão" },
  { code: "1-242", name: "Bahamas" },
  { code: "1-246", name: "Barbados" },
  { code: "32", name: "Bélgica" },
  { code: "501", name: "Belize" },
  { code: "375", name: "Bielorrússia" },
  { code: "591", name: "Bolívia" },
  { code: "55", name: "Brasil" },
  { code: "359", name: "Bulgária" },
  { code: "357", name: "Chipre" },
  { code: "86", name: "China" },
  { code: "57", name: "Colômbia" },
  { code: "506", name: "Costa Rica" },
  { code: "385", name: "Croácia" },
  { code: "53", name: "Cuba" },
  { code: "45", name: "Dinamarca" },
  { code: "1-767", name: "Dominica" },
  { code: "503", name: "El Salvador" },
  { code: "593", name: "Equador" },
  { code: "421", name: "Eslováquia" },
  { code: "386", name: "Eslovênia" },
  { code: "34", name: "Espanha" },
  { code: "1", name: "Estados Unidos" },
  { code: "372", name: "Estônia" },
  { code: "358", name: "Finlândia" },
  { code: "33", name: "França" },
  { code: "995", name: "Geórgia" },
  { code: "350", name: "Gibraltar" },
  { code: "30", name: "Grécia" },
  { code: "1-473", name: "Granada" },
  { code: "502", name: "Guatemala" },
  { code: "592", name: "Guiana" },
  { code: "509", name: "Haiti" },
  { code: "504", name: "Honduras" },
  { code: "36", name: "Hungria" },
  { code: "353", name: "Irlanda" },
  { code: "354", name: "Islândia" },
  { code: "39", name: "Itália" },
  { code: "1-876", name: "Jamaica" },
  { code: "81", name: "Japão" },
  { code: "371", name: "Letônia" },
  { code: "423", name: "Liechtenstein" },
  { code: "370", name: "Lituânia" },
  { code: "352", name: "Luxemburgo" },
  { code: "356", name: "Malta" },
  { code: "52", name: "México" },
  { code: "373", name: "Moldávia" },
  { code: "377", name: "Mônaco" },
  { code: "382", name: "Montenegro" },
  { code: "505", name: "Nicarágua" },
  { code: "47", name: "Noruega" },
  { code: "31", name: "Países Baixos" },
  { code: "507", name: "Panamá" },
  { code: "595", name: "Paraguai" },
  { code: "51", name: "Peru" },
  { code: "48", name: "Polônia" },
  { code: "1-787", name: "Porto Rico" },
  { code: "351", name: "Portugal" },
  { code: "44", name: "Reino Unido" },
  { code: "420", name: "República Tcheca" },
  { code: "1-809", name: "República Dominicana" },
  { code: "40", name: "Romênia" },
  { code: "7", name: "Rússia" },
  { code: "378", name: "San Marino" },
  { code: "1-869", name: "São Cristóvão e Nevis" },
  { code: "1-758", name: "Santa Lúcia" },
  { code: "1-784", name: "São Vicente e Granadinas" },
  { code: "381", name: "Sérvia" },
  { code: "46", name: "Suécia" },
  { code: "41", name: "Suíça" },
  { code: "597", name: "Suriname" },
  { code: "1-868", name: "Trinidad e Tobago" },
  { code: "90", name: "Turquia" },
  { code: "380", name: "Ucrânia" },
  { code: "598", name: "Uruguai" },
  { code: "379", name: "Vaticano" },
  { code: "58", name: "Venezuela" },
]);
const defaultCountry =
  countries.value.find((c) => c.code === "55") || countries.value[0];
const phoneParts = ref({
  selectedCountry: defaultCountry,
  ddd: "",
  number: "",
});
const ddiSearchTerm = ref(`${defaultCountry.name} (+${defaultCountry.code})`);
const isAutocompleteVisible = ref(false);
const showPassword = ref(false);
const showConfirmPassword = ref(false);

// Lógica dos checkboxes de WhatsApp
watch(
  () => user.value.phoneIsWhatsapp,
  (isWhatsapp) => {
    if (isWhatsapp === false) {
      user.value.isWhatsappPublic = false;
    }
  }
);

watch(
  () => user.value.isWhatsappPublic,
  (isPublic) => {
    if (isPublic === true) {
      user.value.phoneIsWhatsapp = true;
    }
  }
);

// Validação visual de senhas
const passwordsMismatch = computed(() => {
  const p = user.value.password?.trim();
  const c = user.value.confirmPassword?.trim();
  return p && c && p !== c;
});

// Refs
const fullNameInputRef = ref(null);
// ── PATCH: ref do campo "Nome de Usuário" — foco ao entrar no step 2 ──
const userNameInputRef = ref(null);
// ── FIM PATCH ────────────────────────────────────────────────────────
const emailInputRef = ref(null);
const ddiAutocompleteRef = ref(null);
const codeInputRefs = ref([]);

// Filtragem países
const filteredCountries = computed(() => {
  if (!ddiSearchTerm.value) {
    return countries.value;
  }
  const searchTerm = ddiSearchTerm.value.toLowerCase();
  return countries.value.filter(
    (country) =>
      country.name.toLowerCase().includes(searchTerm) ||
      country.code.includes(searchTerm)
  );
});

// Validação de telefone
const isPhoneValid = computed(() => {
  const selectedCountry = phoneParts.value.selectedCountry;
  const ddiIsSet = selectedCountry && ddiSearchTerm.value.trim() !== "";
  const dddIsSet = phoneParts.value.ddd.trim().length >= 2;
  const numberIsSet = phoneParts.value.number.replace(/\D/g, "").length >= 8;
  return ddiIsSet && dddIsSet && numberIsSet;
});

// Validação por etapa
const isStep1Valid = computed(() => {
  return (
    (!props.isSchoolSignup || institutionName.value.trim() !== "") &&
    user.value.fullName.trim() !== "" &&
    user.value.birthDate.trim() !== "" &&
    isAdult.value
  );
});

const passwordCriteriaMet = computed(() => {
  const password = user.value.password || "";
  return {
    length: password.length >= 8,
    letter: /[a-zA-Z]/.test(password),
    number: /\d/.test(password),
  };
});


// ── Conta já existente com este e-mail ──────────────────────────────────────
// Consultado ao sair do campo de e-mail (POST /auth/register/precheck). Um
// e-mail "claimable" é de um membro de círculo sem assinatura própria: o
// cadastro reaproveita a conta dele - nada muda até o código ser confirmado,
// nome e data de nascimento seguem os gravados, e o passo de redes sociais é
// pulado. Antes, a pessoa preenchia tudo e perdia tudo em silêncio.
const emailPrecheck = ref({ email: "", status: null });
const isCheckingEmail = ref(false);

function normalizedEmail() {
  return user.value.email.trim().toLowerCase();
}
const emailPrecheckApplies = computed(
  () => !!emailPrecheck.value.status && emailPrecheck.value.email === normalizedEmail()
);
const isClaimingAccount = computed(
  () => emailPrecheckApplies.value && emailPrecheck.value.status === "claimable"
);
const isEmailTaken = computed(
  () => emailPrecheckApplies.value && emailPrecheck.value.status === "taken"
);

async function checkRegistrationEmail() {
  const email = normalizedEmail();
  if (!email || validateEmailFormat(email) !== "") return;
  // Só uma resposta válida é lembrada: se a consulta falhou, tenta de novo na
  // próxima saída do campo ou no "Avançar".
  if (emailPrecheck.value.email === email && emailPrecheck.value.status) return;
  isCheckingEmail.value = true;
  try {
    const response = await userService.precheckRegistrationEmail(email);
    emailPrecheck.value = { email, status: response.data?.status ?? null };
  } catch (e) {
    // Sem a consulta o cadastro segue como antes: o envio final responde.
    console.warn("[UserRegistrationForm] Falha no precheck de e-mail:", e.response?.data?.message || e.message);
    emailPrecheck.value = { email, status: null };
  } finally {
    isCheckingEmail.value = false;
  }
}

function validateEmailFormat(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) ? "" : "inválido";
}

const isEmailStepValid = computed(
  () => validateEmailFormat(normalizedEmail()) === "" && !isCheckingEmail.value
);

const isStep2Valid = computed(() => {
  const criteria = passwordCriteriaMet.value;
  const allCriteriaMet = criteria.length && criteria.letter && criteria.number;
  // Conta reaproveitada: só senha e termos - o resto é da conta que já existe.
  if (isClaimingAccount.value) {
    return (
      allCriteriaMet &&
      user.value.password === user.value.confirmPassword &&
      user.value.agreedToTerms
    );
  }
  return (
    user.value.userName.trim() !== "" &&
    user.value.email.trim() !== "" &&
    !isEmailTaken.value &&
    allCriteriaMet &&
    user.value.password === user.value.confirmPassword &&
    user.value.agreedToTerms &&
    isPhoneValid.value
  );
});

// Logo após passwordCriteriaMet, antes de isStep2Valid
const isAdult = computed(() => {
  if (!user.value.birthDate) return false;
  const birth = new Date(user.value.birthDate);
  const today = new Date();
  const age18 = new Date(
    birth.getFullYear() + 18,
    birth.getMonth(),
    birth.getDate()
  );
  return age18 <= today;
});

const maxBirthDate = computed(() => {
  const d = new Date();
  d.setFullYear(d.getFullYear() - 18);
  return d.toISOString().split("T")[0]; // "YYYY-MM-DD"
});

// Máscaras telefone
function onDddInput() {
  phoneParts.value.ddd = phoneParts.value.ddd.replace(/\D/g, "");
}
function onNumberInput() {
  phoneParts.value.number = phoneParts.value.number.replace(/\D/g, "");
}
function handlePhoneBlur() {
  const { number } = phoneParts.value;
  const cleanNumber = number.replace(/\D/g, "");
  if (cleanNumber.length > 5) {
    phoneParts.value.number =
      cleanNumber.slice(0, cleanNumber.length - 4) +
      "-" +
      cleanNumber.slice(cleanNumber.length - 4);
  }
}

// Código de verificação
const verificationCode = ref(new Array(6).fill(""));

// ── PATCH: auto-envio quando o código atinge 6 dígitos (independente do índice) ──
const isCodeComplete = computed(() =>
  verificationCode.value.every((digit) => digit !== "")
);

watch(isCodeComplete, (complete) => {
  if (complete && !isLoading.value) {
    submitVerification();
  }
});
// ── FIM PATCH ──────────────────────────────────────────────────────────────────

// ── PATCH: estado do cooldown de reenvio do código (passo 4) ─────────────────
const resendCooldown = ref(0);
const resendTimerId = ref(null);
const isResending = ref(false);

function startResendCooldown() {
  // ── PATCH: encaminha para a variante parametrizável com 60s default ──
  startResendCooldownWithSeconds(60);
  // ── FIM PATCH ─────────────────────────────────────────────────────────
}

// ── PATCH: variante que aceita um número arbitrário de segundos ───────────
// Usada quando o backend retorna 429 com Retry-After diferente de 60s
// (ex.: limite 5/15min atingido pode retornar centenas de segundos).
function startResendCooldownWithSeconds(seconds) {
  if (resendTimerId.value) {
    clearInterval(resendTimerId.value);
    resendTimerId.value = null;
  }
  resendCooldown.value = Math.max(1, Math.ceil(seconds));
  resendTimerId.value = setInterval(() => {
    resendCooldown.value -= 1;
    if (resendCooldown.value <= 0) {
      clearInterval(resendTimerId.value);
      resendTimerId.value = null;
      // NÃO limpa os inputs aqui — código no backend continua válido por 15min.
    }
  }, 1000);
}
// ── FIM PATCH ──────────────────────────────────────────────────────────────

// ── PATCH: helper de exibição do tempo do cooldown ─────────────────────────
// Até 90s exibe "60s"; acima disso exibe "2min", "3min" etc. para evitar
// números grandes (ex.: 13min15s do limite 5/15min).
function formatCooldown(totalSeconds) {
  if (totalSeconds <= 90) return `${totalSeconds}s`;
  const minutes = Math.ceil(totalSeconds / 60);
  return `${minutes}min`;
}
// ── FIM PATCH ──────────────────────────────────────────────────────────────

async function resendVerificationCode() {
  if (isResending.value || resendCooldown.value > 0) return;
  isResending.value = true;
  try {
    await userStore.resendVerificationCode(user.value.email);
    // Sucesso: limpa inputs (estado + DOM), foca no primeiro, toast e reinicia cooldown
    verificationCode.value.fill("");
    // ── PATCH: limpar também o value do DOM (inputs são @input-driven, não :value) ──
    codeInputRefs.value.forEach((input) => {
      if (input) input.value = "";
    });
    // ── FIM PATCH ────────────────────────────────────────────────────────────────
    if (codeInputRefs.value[0]) codeInputRefs.value[0].focus();
    // ── PATCH: toast explicita que o código anterior deixa de valer ──
    showSuccessToast(
      "Novo código enviado. O código anterior não é mais válido."
    );
    // ── FIM PATCH ──────────────────────────────────────────────────────
    startResendCooldown();
  } catch (error) {
    // ── PATCH: tratamento específico de rate limit (HTTP 429) ──────────────────
    // Quando o backend bloqueia (cooldown server-side ou limite 5/15min), ajusta
    // o cooldown local para o valor real retornado pelo servidor (Retry-After).
    // Isso sincroniza o estado UI com a realidade do backend mesmo se o usuário
    // recarregar a página, ou se houver tentativas externas (curl, outra aba).
    if (error.code === "RATE_LIMIT") {
      startResendCooldownWithSeconds(error.retryAfterSeconds);
      showErrorToast(error.message);
    } else {
      // Erro genérico: NÃO reinicia cooldown e NÃO limpa inputs — usuário pode tentar de novo
      showErrorToast(
        error.message ||
          "Não foi possível enviar um novo código. Tente novamente."
      );
    }
    // ── FIM PATCH ────────────────────────────────────────────────────────────────
  } finally {
    isResending.value = false;
  }
}
// ── FIM PATCH ────────────────────────────────────────────────────────────────

function handleCodeInput(event, index) {
  const input = event.target;
  if (!/^\d$/.test(input.value)) {
    input.value = "";
    verificationCode.value[index] = "";
    return;
  }
  verificationCode.value[index] = input.value;
  if (input.value && index < 5) {
    codeInputRefs.value[index + 1].focus();
  }
  // ── PATCH: auto-envio agora é tratado pelo watch(isCodeComplete) — removido daqui ──
}
function handleCodeBackspace(event, index) {
  if (verificationCode.value[index]) {
    verificationCode.value[index] = "";
  } else if (!event.target.value && index > 0) {
    codeInputRefs.value[index - 1].focus();
  }
}

// Navegação e submissão
async function advanceStep() {
  if (isLoading.value) return;

  // Passo 0: o e-mail decide o caminho.
  //  - livre        -> cadastro completo (Pessoais, Conta, Redes, Código)
  //  - reaproveitável -> só senha + termos (passo 2 enxuto) -> Código
  //  - ocupado      -> mensagem com "Entrar" / "Esqueci minha senha", sem avançar
  // Se a consulta falhar, segue como cadastro completo: o envio final responde.
  if (registrationStep.value === 0) {
    if (!isEmailStepValid.value) {
      showErrorToast("Informe um e-mail válido.");
      return;
    }
    await checkRegistrationEmail();
    if (isEmailTaken.value) return;
    if (isClaimingAccount.value) {
      registrationStep.value = 2;
      return;
    }
    registrationStep.value = 1;
    return;
  }

  // Conta reaproveitada: do passo 2 (senha) direto para o envio. Redes
  // sociais não seriam gravadas mesmo (a conta existente não muda).
  if (registrationStep.value === 2 && isClaimingAccount.value) {
    if (!isStep2Valid.value) {
      showErrorToast("Preencha os campos obrigatórios.");
      return;
    }
    await submitRegistration();
    return;
  }

  if (registrationStep.value === 3) {
    await submitRegistration();
    return;
  }

  if (registrationStep.value < 4) {
    if (registrationStep.value === 1 && !isStep1Valid.value) {
      showErrorToast("Preencha os campos obrigatórios.");
      return;
    }
    if (registrationStep.value === 1 && props.isSchoolSignup) {
      onboardingStore.setInstitutionName(institutionName.value.trim() || null);
    }
    if (registrationStep.value === 2 && !isStep2Valid.value) {
      showErrorToast("Preencha os campos obrigatórios.");
      return;
    }
    registrationStep.value++;
  }
}

async function submitRegistration() {
  {
    if (!isClaimingAccount.value && !isPhoneValid.value) {
      showErrorToast("O telefone (DDI, DDD e Número) é obrigatório.");
      return;
    }

    isLoading.value = true;
    try {
      const { selectedCountry, ddd, number } = phoneParts.value;
      const cleanDDI = selectedCountry.code.replace(/\D/g, "");
      const cleanNumber = number.replace(/\D/g, "");
      const formattedPhone = `+${cleanDDI}${ddd}${cleanNumber}`;

      const registrationPayload = {
        // Conta reaproveitada não pede nome de usuário (fica o já gravado), mas
        // o DTO do backend exige o campo: vai o e-mail, que é descartado lá.
        userName: isClaimingAccount.value ? normalizedEmail() : user.value.userName,
        fullName: isClaimingAccount.value ? null : user.value.fullName,
        // Cadastro de escola: o backend grava em users.institution_name e, por
        // ele, atribui o plano "Edu Free" em vez do "Free" individual.
        institutionName: props.isSchoolSignup
          ? institutionName.value.trim() || null
          : null,
        birthDate: isClaimingAccount.value ? null : user.value.birthDate,
        country: user.value.country,
        profession: user.value.profession,
        // Conta reaproveitada não muda nada além da senha; telefone nem vai.
        phoneE164: isClaimingAccount.value ? null : formattedPhone,
        email: user.value.email,
        password: user.value.password,
        phoneIsWhatsapp: user.value.phoneIsWhatsapp,
        isWhatsappPublic: user.value.isWhatsappPublic,
        telegramUserName: user.value.telegramUserName,
        instagramUserName: user.value.instagramUserName,
        linkedInUserName: user.value.linkedInUserName,
        facebookUserName: user.value.facebookUserName,
        groupIds: groupIds.value,
        status: 0,
      };

      if (!registrationPayload.groupIds.length) {
        throw new Error("Plano de registro não encontrado.");
      }

      await userStore.registerUser(registrationPayload);
      registrationStep.value = 4;
    } catch (error) {
      //console.log("error completo:", error);
      const responseData = error.response?.data; // [PATCH] renomeado de data para responseData
      let msg = "Erro ao registrar usuário.";

      if (responseData?.errors) {
        const messages = Object.values(responseData.errors).flat();
        if (messages.length > 0) msg = messages.join("\n");
      } else if (responseData?.message) {
        msg = responseData.message;
      } else if (responseData?.title) {
        msg = responseData.title;
      } else if (error.message) {
        msg = error.message;
      }

      showErrorToast(msg);
    } finally {
      isLoading.value = false;
    }
  }
}

function goBackStep() {
  // Passo 0 (e-mail) é o primeiro: voltar fecha o modal e retorna ao LoginForm.
  if (registrationStep.value <= 0) {
    emit("close");
    return;
  }
  // Conta reaproveitada pula o passo 1: do passo 2 volta ao e-mail.
  if (registrationStep.value === 2 && isClaimingAccount.value) {
    registrationStep.value = 0;
    return;
  }
  registrationStep.value--;
}

// Indicador de passos. No cadastro completo são cinco; na conta reaproveitada
// só três (e-mail, senha, código), para o desenho não prometer passos que
// não vão acontecer.
const stepperItems = computed(() => {
  if (isClaimingAccount.value) {
    return [
      { label: "1. E-mail", active: registrationStep.value >= 0 },
      { label: "2. Senha", active: registrationStep.value >= 2 },
      { label: "3. Confirmação", active: registrationStep.value === 4 },
    ];
  }
  return [
    { label: "1. E-mail", active: registrationStep.value >= 0 },
    { label: "2. Pessoais", active: registrationStep.value >= 1 },
    { label: "3. Conta", active: registrationStep.value >= 2 },
    { label: "4. Redes Sociais", active: registrationStep.value >= 3 },
    { label: "5. Confirmação", active: registrationStep.value === 4 },
  ];
});

async function submitVerification() {
  if (isLoading.value) return;

  const finalCode = verificationCode.value.join("");
  if (finalCode.length !== 6) {
    showErrorToast("Por favor, insira o código de verificação de 6 dígitos.");
    return;
  }

  isLoading.value = true;

  try {
    const verificationData = { email: user.value.email, code: finalCode };
    await userStore.verifyUserEmail(verificationData);

    trackCompleteRegistration();

    showSuccessToast("Conta verificada com sucesso!");

    try {
      const loginData = await userStore.login({
        email: user.value.email,
        password: user.value.password,
        rememberMe: false,
      });
      // Conta reaproveitada: agora existem "Minha conta" e "Membro de X". Quem
      // acabou de fazer um cadastro quer a conta pessoal, não a escola.
      if (loginData?.requiresContextSelection) {
        await userStore.confirmLoginContext(null);
      }
    } catch (e) {
      console.error(
        "[UserRegistrationForm] Falha no auto-login pós-verificação:",
        e
      );

      isLoading.value = false;

      showErrorToast(
        "Conta verificada, mas não foi possível entrar automaticamente. Faça login para continuar."
      );

      setTimeout(() => {
        emit("close");
      }, 2200);

      return;
    }

    // Escola vai direto ao assistente de configuração (papéis → pessoas). O
    // modal de boas-vindas fala do plano Free individual, que não é o dela: a
    // conta já nasceu no "Edu Free" (ver institutionName no payload) e o
    // caminho natural é cadastrar professores e alunos.
    if (props.isSchoolSignup && !isMobile) {
      isLoading.value = false;
      emit("close");
      router.replace({ name: "SchoolSetupWizard" });
      return;
    }

    // ── PATCH: exibe modal de boas-vindas adequado ao dispositivo.
    setTimeout(() => {
      isLoading.value = false;
      if (isMobile) {
        showWelcomeMobileModal.value = true;
      } else {
        showWelcomeFreePlanModal.value = true;
      }
    }, 800);
    // ── FIM PATCH ───────────────────────────────────────────────────────────
  } catch (error) {
    console.error("Erro na verificação:", error);
    showErrorToast(
      error.message ||
        "Não foi possível verificar sua conta. O código pode estar incorreto ou ter expirado."
    );
    verificationCode.value.fill("");
    // ── PATCH: limpar também o value do DOM (inputs são @input-driven, não :value) ──
    codeInputRefs.value.forEach((input) => {
      if (input) input.value = "";
    });
    // ── FIM PATCH ────────────────────────────────────────────────────────────────
    if (codeInputRefs.value[0]) codeInputRefs.value[0].focus();
    isLoading.value = false;
  }
}

function closeModal() {
  emit("close");
}

// ── PATCH: ações do modal de boas-vindas pós-verificação ───────────────────
function handleWelcomeStart() {
  showWelcomeFreePlanModal.value = false;
  emit("close");
  // Conta nova entra pela tela de primeiro acesso (mesmo destino do login
  // com onboarding pendente); de lá a pessoa escolhe por onde começar.
  router.replace({ name: "FirstAccessView" });
}

function handleWelcomePlans() {
  showWelcomeFreePlanModal.value = false;
  emit("close");
  router.replace({ name: "UpgradePage" });
}

function handleWelcomeMobileClose() {
  showWelcomeMobileModal.value = false;
  emit("close");
}
// ── FIM PATCH ───────────────────────────────────────────────────────────────

// Autocomplete DDI
function selectCountry(country) {
  phoneParts.value.selectedCountry = country;
  ddiSearchTerm.value = `${country.name} (+${country.code})`;
  isAutocompleteVisible.value = false;
}
function handleDdiFocus() {
  ddiSearchTerm.value = "";
  isAutocompleteVisible.value = true;
}
function handleDdiBlur() {
  setTimeout(() => {
    isAutocompleteVisible.value = false;
    const isValidCountrySelection = countries.value.some(
      (c) => `${c.name} (+${c.code})` === ddiSearchTerm.value
    );
    if (!ddiSearchTerm.value.trim() || !isValidCountrySelection) {
      const selected = phoneParts.value.selectedCountry;
      ddiSearchTerm.value = `${selected.name} (+${selected.code})`;
    }
  }, 200);
}
const handleClickOutside = (event) => {
  if (
    ddiAutocompleteRef.value &&
    !ddiAutocompleteRef.value.contains(event.target)
  ) {
    isAutocompleteVisible.value = false;
  }
};

// Hooks
onMounted(async () => {
  user.value.facebookUserName = "";
  user.value.password = "";
  user.value.confirmPassword = "";

  // [PATCH] registrationStep começa em 0 mas avança automaticamente para 1
  // na primeira chamada de advanceStep (via botão "Avançar ao login"),
  // porém para que o modal já abra no step 1 sem precisar de clique extra,
  // inicializamos direto em 1. usageType vira "Edu" quando veio do cadastro
  // de escola (isSchoolSignup), senão fica "indiferente" como antes.
  onboardingStore.setUsageType(props.isSchoolSignup ? "Edu" : "indiferente");
  registrationStep.value = 0;

  try {
    // GET /groups exige login e filtra por círculo — não dá pra usar aqui,
    // porque quem está se cadastrando ainda não está autenticado. Por isso
    // o grupo "Todos" é resolvido por um endpoint público dedicado
    // (GET /groups/by-name/{nome}), em vez de listar+filtrar.
    const response = await groupService.getGroupByName("Todos");
    trialPlanGroupId.value = response?.data?.groupId ?? null;
  } catch (error) {
    console.error("Erro ao buscar o grupo 'Todos':", error);
    showErrorToast(
      "Não foi possível carregar as configurações de plano. Por favor, tente novamente."
    );
  }

  nextTick(() => fullNameInputRef.value?.focus());
  document.addEventListener("click", handleClickOutside);
});

onUnmounted(() => {
  document.removeEventListener("click", handleClickOutside);
  if (toastTimeoutId.value) clearTimeout(toastTimeoutId.value);
  // ── PATCH: limpa o timer do cooldown de reenvio se ainda estiver ativo ──
  if (resendTimerId.value) {
    clearInterval(resendTimerId.value);
    resendTimerId.value = null;
  }
  // ── FIM PATCH ─────────────────────────────────────────────────────────────
});

watch(registrationStep, (newStep) => {
  nextTick(() => {
    if (newStep === 0) emailInputRef.value?.focus();
    else if (newStep === 1) fullNameInputRef.value?.focus();
    // ── PATCH: step 2 foca no campo "Nome de Usuário" (primeiro do step), não no e-mail ──
    else if (newStep === 2) userNameInputRef.value?.focus();
    // ── FIM PATCH ──────────────────────────────────────────────────────────────────────
    else if (newStep === 4) {
      codeInputRefs.value[0]?.focus();
      // ── PATCH: inicia cooldown de reenvio ao entrar no passo de confirmação ──
      startResendCooldown();
      // ── FIM PATCH ──────────────────────────────────────────────────────────
    }
  });
});
</script>

<template>
  <div class="modal-backdrop">
    <!-- ── PATCH: coluna esquerda (.plan-info-column) removida; painel agora é de coluna única ── -->
    <div class="modal-panel">
      <div class="form-column">
        <div class="modal-header">
          <!-- ── PATCH: logomarca inserida à esquerda do título do cabeçalho ── -->
          <div class="modal-title-group">
            <img
              src="/litterae_pena_fundo_transparente.png"
              alt="Logo Litterae"
              class="modal-logo"
            />
            <h3 class="modal-title">Crie sua conta</h3>
          </div>
          <!-- ── FIM PATCH ──────────────────────────────────────────────────── -->
          <button @click="closeModal" type="button" class="modal-close-btn">
            <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path
                fill-rule="evenodd"
                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                clip-rule="evenodd"
              ></path>
            </svg>
          </button>
        </div>

        <form @submit.prevent="submitVerification">
          <!-- Campos isca para autofill -->
          <input
            type="text"
            name="fake-username"
            autocomplete="username"
            style="
              position: absolute;
              left: -9999px;
              opacity: 0;
              height: 0;
              width: 0;
              border: 0;
              padding: 0;
              margin: 0;
            "
            tabindex="-1"
          />
          <input
            type="password"
            name="fake-password"
            autocomplete="new-password"
            style="
              position: absolute;
              left: -9999px;
              opacity: 0;
              height: 0;
              width: 0;
              border: 0;
              padding: 0;
              margin: 0;
            "
            tabindex="-1"
          />

          <div class="modal-body">
            <!-- [PATCH] Breadcrumb sempre visível (step 0 eliminado) -->
            <div class="stepper-container">
              <template v-for="(item, i) in stepperItems" :key="item.label">
                <div v-if="i > 0" class="step-divider"></div>
                <div :class="['step', { active: item.active }]">{{ item.label }}</div>
              </template>
            </div>

            <!-- STEP 0: E-MAIL -->
            <div v-if="registrationStep === 0" class="step-content">
              <h2 class="step-title">Seu e-mail</h2>
              <div class="form-group">
                <label for="email" class="form-label">E-mail</label>
                <input
                  ref="emailInputRef"
                  v-model="user.email"
                  type="email"
                  id="email"
                  class="form-input half-input"
                  placeholder="seuemail@dominio.com"
                  required
                  @blur="checkRegistrationEmail"
                  @keydown.enter.prevent="advanceStep"
                />
                <div v-if="isEmailTaken" class="taken-notice" role="alert">
                  <p>
                    <strong>Este e-mail já tem uma conta no Litterae.</strong>
                    Se for sua, entre com ela; se não lembra a senha, peça uma nova.
                  </p>
                  <div class="taken-actions">
                    <button type="button" class="btn btn-primary" @click="emit('login-with', normalizedEmail())">
                      Entrar
                    </button>
                    <button type="button" class="btn btn-secondary" @click="emit('forgot-password', normalizedEmail())">
                      Esqueci minha senha
                    </button>
                  </div>
                </div>
                <div v-else-if="isClaimingAccount" class="claim-notice" role="status">
                  <strong>Este e-mail já tem uma conta no Litterae</strong> como membro
                  de uma instituição. Você só precisa escolher uma senha e confirmar o
                  código que vamos enviar; depois disso a conta passa a ter também o seu
                  espaço pessoal. Nome e data de nascimento continuam os já cadastrados.
                </div>
              </div>
            </div>

            <!-- STEP 1: PESSOAIS -->
            <div v-if="registrationStep === 1" class="step-content">
              <h2 class="step-title">Informações Pessoais</h2>
              <div v-if="isSchoolSignup" class="form-group">
                <label for="institutionName" class="form-label"
                  >Nome da instituição</label
                >
                <input
                  v-model="institutionName"
                  type="text"
                  id="institutionName"
                  class="form-input short-input"
                  placeholder="Ex.: Colégio Ananery"
                  required
                />
              </div>
              <div class="form-group">
                <label for="fullName" class="form-label">{{
                  isSchoolSignup
                    ? "Nome completo do responsável pela conta"
                    : "Nome Completo"
                }}</label>
                <input
                  ref="fullNameInputRef"
                  v-model="user.fullName"
                  type="text"
                  id="fullName"
                  class="form-input short-input"
                  placeholder="Digite seu nome completo"
                  required
                />
              </div>
              <div class="form-group">
                <label for="birthDate" class="form-label"
                  >Data de Nascimento</label
                >
                <input
                  v-model="user.birthDate"
                  type="date"
                  id="birthDate"
                  class="form-input short-input"
                  :max="maxBirthDate"
                  required
                />
                <span
                  v-if="user.birthDate && !isAdult"
                  class="field-error"
                  role="alert"
                >
                  Você precisa ter 18 anos ou mais para se cadastrar. Neste mesmo cadastro, um adulto poderá adicionar dependentes menores de idade.
                </span>
              </div>
              <div class="form-group">
                <label for="country" class="form-label">País (opcional)</label>
                <input
                  v-model="user.country"
                  type="text"
                  id="country"
                  class="form-input short-input"
                  placeholder="Ex: Brasil"
                />
              </div>
              <div class="form-group">
                <label for="profession" class="form-label"
                  >Profissão (opcional)</label
                >
                <input
                  v-model="user.profession"
                  type="text"
                  id="profession"
                  class="form-input short-input"
                  placeholder="Ex: Desenvolvedor"
                />
              </div>
            </div>

            <!-- STEP 2: CONTA -->
            <div v-if="registrationStep === 2" class="step-content">
              <h2 class="step-title">
                {{ isClaimingAccount ? "Escolha sua senha" : "Informações da Conta" }}
              </h2>
              <p class="email-summary">
                E-mail: <strong>{{ user.email.trim() }}</strong>
              </p>
              <div v-if="isClaimingAccount" class="claim-notice" role="status">
                <strong>Este e-mail já tem uma conta no Litterae</strong> como membro
                de uma instituição. Escolha uma senha e confirme o código que vamos
                enviar; depois disso a conta passa a ter também o seu espaço pessoal.
                Nome e data de nascimento continuam os já cadastrados.
              </div>
              <div v-if="!isClaimingAccount" class="form-group">
                <label for="userName" class="form-label">Nome de Usuário</label>
                <input
                  ref="userNameInputRef"
                  v-model="user.userName"
                  type="text"
                  id="userName"
                  class="form-input half-input"
                  placeholder="Escolha um nome de usuário"
                  required
                />
              </div>
              <div v-if="!isClaimingAccount" class="form-group">
                <label for="phone" class="form-label">Telefone</label>
                <div class="phone-row" @blur="handlePhoneBlur">
                  <div class="phone-group">
                    <div class="ddi-autocomplete" ref="ddiAutocompleteRef">
                      <input
                        type="text"
                        class="form-input ddi-input"
                        v-model="ddiSearchTerm"
                        @focus="handleDdiFocus"
                        @blur="handleDdiBlur"
                        placeholder="País (+código)"
                        required
                      />
                      <ul v-if="isAutocompleteVisible" class="ddi-options">
                        <li
                          v-for="country in filteredCountries"
                          :key="country.code"
                          @mousedown.prevent="selectCountry(country)"
                        >
                          <span>{{ country.name }} (+{{ country.code }})</span>
                        </li>
                        <li
                          v-if="!filteredCountries.length"
                          class="ddi-no-results"
                        >
                          Nenhum país encontrado.
                        </li>
                      </ul>
                    </div>
                    <input
                      v-model="phoneParts.ddd"
                      @input="onDddInput"
                      type="tel"
                      id="ddd"
                      class="form-input ddd-input"
                      placeholder="DDD"
                      maxlength="2"
                      required
                    />
                    <input
                      v-model="phoneParts.number"
                      @input="onNumberInput"
                      type="tel"
                      id="phone"
                      class="form-input phone-input phone-input-compact"
                      placeholder="digite"
                      maxlength="9"
                      required
                    />
                  </div>
                </div>
              </div>
              <div class="credentials-row">
                <div class="form-group flex-1">
                  <label for="password" class="form-label">Senha</label>
                  <div class="password-wrapper">
                    <input
                      v-model="user.password"
                      :type="showPassword ? 'text' : 'password'"
                      id="password"
                      class="form-input"
                      :class="{ 'input-error': passwordsMismatch }"
                      placeholder=""
                      required
                    />
                    <button
                      type="button"
                      @click="showPassword = !showPassword"
                      class="password-toggle-btn"
                    >
                      <svg
                        v-if="!showPassword"
                        class="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.542-7 1.274-4.057 5.064-7 9.542-7 1.422 0 2.774.376 3.982 1.025M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                        ></path>
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M17.938 17.938l-12-12"
                        ></path>
                      </svg>
                      <svg
                        v-else
                        class="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                        ></path>
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268-2.943 9.542-7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                        ></path>
                      </svg>
                    </button>
                  </div>
                </div>
                <div class="form-group flex-1">
                  <label for="confirmPassword" class="form-label label-row">
                    Confirme sua Senha
                    <span
                      v-if="passwordsMismatch"
                      class="label-inline-error"
                      role="alert"
                      aria-live="polite"
                    >
                      • Não coincidem
                    </span>
                  </label>
                  <div class="password-wrapper">
                    <input
                      v-model="user.confirmPassword"
                      :type="showConfirmPassword ? 'text' : 'password'"
                      id="confirmPassword"
                      class="form-input"
                      :class="{ 'input-error': passwordsMismatch }"
                      placeholder=""
                      required
                    />
                    <button
                      type="button"
                      @click="showConfirmPassword = !showConfirmPassword"
                      class="password-toggle-btn"
                    >
                      <svg
                        v-if="!showConfirmPassword"
                        class="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.542-7 1.274-4.057 5.064-7 9.542-7 1.422 0 2.774.376 3.982 1.025M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                        ></path>
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M17.938 17.938l-12-12"
                        ></path>
                      </svg>
                      <svg
                        v-else
                        class="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                        ></path>
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268-2.943 9.542-7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                        ></path>
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
              <div class="password-hints" aria-live="polite">
                Use
                <span :class="{ met: passwordCriteriaMet.length }"
                  >8+ caracteres</span
                >
                com
                <span :class="{ met: passwordCriteriaMet.letter }">letras</span>
                e
                <span :class="{ met: passwordCriteriaMet.number }">números</span
                >.
              </div>
              <div
                v-if="passwordsMismatch"
                class="inline-error"
                role="alert"
                aria-live="polite"
              >
                <svg
                  class="inline-error-icon"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M12 9v4m0 4h.01M12 3l9.196 16H2.804L12 3z"
                  />
                </svg>
                <span>As senhas não coincidem. Confira e tente novamente.</span>
              </div>

              <!-- ── PATCH: links substituídos por botões que abrem o LegalDocsModal ── -->
              <div class="terms-group">
                <input
                  type="checkbox"
                  id="terms"
                  v-model="user.agreedToTerms"
                  class="terms-checkbox"
                  required
                />
                <label for="terms" class="terms-label">
                  Eu li e concordo com os
                  <button
                    type="button"
                    class="terms-link-btn terms-link-btn--inherit"
                    @click.stop="openLegalModal('terms')"
                  >Termos de Uso</button>
                  e a
                  <button
                    type="button"
                    class="terms-link-btn terms-link-btn--inherit"
                    @click.stop="openLegalModal('privacy')"
                  >Política de Privacidade</button>.
                </label>
              </div>
              <!-- ── FIM PATCH ───────────────────────────────────────────────────────── -->

            </div>

            <!-- STEP 3: REDES SOCIAIS -->
            <div v-if="registrationStep === 3" class="step-content">
              <h2 class="step-title">Redes Sociais</h2>
              <div class="contact-options-row">
                <div class="whatsapp-inline">
                  <input
                    type="checkbox"
                    id="phoneIsWhatsapp"
                    v-model="user.phoneIsWhatsapp"
                    class="terms-checkbox"
                  />
                  <label
                    for="phoneIsWhatsapp"
                    class="terms-label flex items-center gap-1"
                  >
                    <!-- ── PATCH: removidos o ponto final e o ícone do WhatsApp ── -->
                    O telefone informado é meu WhatsApp
                    <!-- ── FIM PATCH ──────────────────────────────────────────── -->
                  </label>
                </div>
              </div>
              <div class="whatsapp-public-row">
                <div class="whatsapp-checkbox-group">
                  <input
                    type="checkbox"
                    id="isWhatsappPublic"
                    v-model="user.isWhatsappPublic"
                    class="terms-checkbox"
                  />
                  <label for="isWhatsappPublic" class="terms-label"
                    >Autorizo exibir meu WhatsApp no site</label
                  >
                </div>
              </div>
              <div class="social-row">
                <div class="form-group social-input">
                  <label
                    for="telegramUserName"
                    class="form-label flex items-center gap-1"
                  >
                    <i class="fab fa-telegram-plane text-sky-500"></i> Telegram
                  </label>
                  <input
                    v-model="user.telegramUserName"
                    type="text"
                    id="telegramUserName"
                    class="form-input"
                    placeholder="@usuario ou telefone"
                  />
                </div>
                <div class="form-group social-input">
                  <label
                    for="instagramUserName"
                    class="form-label flex items-center gap-1"
                  >
                    <i class="fab fa-instagram text-pink-500"></i> Instagram
                  </label>
                  <input
                    v-model="user.instagramUserName"
                    type="text"
                    id="instagramUserName"
                    class="form-input"
                    placeholder="@usuário"
                  />
                </div>
              </div>
              <div class="form-group social-wide-input">
                <label
                  for="linkedInUserName"
                  class="form-label flex items-center gap-1"
                >
                  <i class="fab fa-linkedin-in text-blue-600"></i> LinkedIn
                </label>
                <input
                  v-model="user.linkedInUserName"
                  type="text"
                  id="linkedInUserName"
                  class="form-input social-wide-control"
                  placeholder="url do perfil"
                />
              </div>
              <div class="form-group social-wide-input">
                <label
                  for="facebookUserName"
                  class="form-label flex items-center gap-1"
                >
                  <i class="fab fa-facebook-f text-blue-700"></i> Facebook
                </label>
                <input
                  v-model="user.facebookUserName"
                  type="text"
                  id="facebookUserName"
                  class="form-input social-wide-control"
                  placeholder="url do perfil"
                />
              </div>
            </div>

            <!-- STEP 4: CONFIRMAÇÃO -->
            <div v-if="registrationStep === 4" class="step-content">
              <h2 class="step-title">Verifique seu endereço de e-mail</h2>
              <p class="confirmation-text">
                Enviamos um código de seis dígitos para
                <strong>{{ user.email }}</strong
                >. Insira o código abaixo para confirmar seu endereço de e-mail.
              </p>
              <div class="code-inputs-container">
                <input
                  v-for="i in 6"
                  :key="i"
                  :ref="
                    (el) => {
                      if (el) codeInputRefs[i - 1] = el;
                    }
                  "
                  @input="handleCodeInput($event, i - 1)"
                  @keydown.delete="handleCodeBackspace($event, i - 1)"
                  type="text"
                  maxlength="1"
                  class="code-input"
                />
              </div>
              <div class="info-box">
                Certifique-se de manter esta janela aberta enquanto verifica sua
                caixa de entrada.
              </div>

              <!-- ── PATCH: bloco de reenvio do código — botão único com barra de progresso ── -->
              <div class="resend-block">
                <button
                  type="button"
                  class="resend-btn"
                  :class="{ 'resend-btn--ready': resendCooldown === 0 && !isResending }"
                  :disabled="resendCooldown > 0 || isResending"
                  @click="resendVerificationCode"
                >
                  <!-- Ícone: relógio durante cooldown, envelope quando pronto -->
                  <svg
                    v-if="resendCooldown > 0"
                    class="resend-btn-icon"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <circle cx="12" cy="12" r="9" />
                    <path d="M12 7v5l3 2" />
                  </svg>
                  <svg
                    v-else
                    class="resend-btn-icon"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <path d="M4 4h16v16H4z" />
                    <path d="M4 4l8 8 8-8" />
                  </svg>

                  <span class="resend-btn-label">
                    <template v-if="isResending">Enviando...</template>
                    <template v-else-if="resendCooldown > 0">
                      Aguarde {{ formatCooldown(resendCooldown) }} para novo código
                    </template>
                    <template v-else>Enviar novo código</template>
                  </span>

                  <!-- Barra de progresso que esvazia conforme o tempo passa -->
                  <span
                    v-if="resendCooldown > 0"
                    class="resend-btn-progress"
                    :style="{ width: ((60 - resendCooldown) / 60) * 100 + '%' }"
                  ></span>
                </button>
              </div>
              <!-- ── FIM PATCH ──────────────────────────────────────────────────────────── -->
            </div>
          </div>

          <div class="modal-footer">
            <!-- [PATCH] "Voltar ao login" do step 0 removido junto com o UsageTypeSelector -->

            <!-- Voltar entre steps — steps 1 em diante -->
            <button
              v-if="registrationStep < 4"
              type="button"
              @click="goBackStep"
              class="btn btn-secondary"
              :disabled="isLoading"
            >
               {{ registrationStep === 0 ? "Voltar ao login" : "Voltar" }}
            </button>

            <!-- Avançar — steps 1 a 3 -->
            <button
              v-if="registrationStep < 4"
              type="button"
              @click="advanceStep"
              class="btn btn-primary"
              :disabled="
                isLoading ||
                (registrationStep === 0 && (!isEmailStepValid || isEmailTaken)) ||
                (registrationStep === 1 && !isStep1Valid) ||
                (registrationStep === 2 && !isStep2Valid)
              "
            >
              {{
                isLoading && (registrationStep === 3 || (registrationStep === 2 && isClaimingAccount))
                  ? "Enviando..."
                  : "Avançar"
              }}
            </button>

            <!-- Finalizar — step 4 -->
            <button
              v-if="registrationStep === 4"
              type="submit"
              class="btn btn-success"
              :disabled="isLoading || !isCodeComplete"
            >
              {{ isLoading ? "Verificando..." : "Finalizar Cadastro" }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>

  <!-- ── PATCH: LegalDocsModal renderizado via Teleport para evitar conflito de z-index ── -->
  <Teleport to="body">
    <LegalDocsModal
      v-if="showLegalModal"
      :defaultDoc="legalModalDefaultDoc"
      @close="closeLegalModal"
    />
    <WelcomeFreePlanModal
      v-if="showWelcomeFreePlanModal"
      @start="handleWelcomeStart"
      @plans="handleWelcomePlans"
    />
    <WelcomeMobileModal
      v-if="showWelcomeMobileModal"
      @close="handleWelcomeMobileClose"
    />
  </Teleport>
  <!-- ── FIM PATCH ──────────────────────────────────────────────────────────────────────── -->

  <Teleport to="body">
    <transition name="toast-fade">
      <div
        v-if="showToast"
        class="fixed inset-0 pointer-events-none z-[9999] flex items-start justify-center pt-6 px-4"
      >
        <div
          :class="[
 'toast pointer-events-auto',
            toastType === 'error' ? 'toast-error' : 'toast-success',
          ]"
        >
          <div class="toast-icon">
            <svg class="w-8 h-8" viewBox="0 0 20 20" fill="none">
              <circle cx="10" cy="10" r="9" class="toast-icon-circle" />
              <path
                v-if="toastType === 'error'"
                d="M10 5v6M10 13.5v1.5"
                stroke="currentColor"
                stroke-width="2.2"
                stroke-linecap="round"
              />
              <path
                v-else
                d="M7.5 10.5 9.5 12.5 13 8.5"
                stroke="currentColor"
                stroke-width="2.2"
                stroke-linejoin="round"
              />
            </svg>
          </div>
          <div class="toast-content">
            <p class="toast-title">
              {{ toastType === "error" ? "Erro no Cadastro" : "Sucesso!" }}
            </p>
            <p class="toast-message">{{ toastMessage }}</p>
          </div>
          <button
            type="button"
            class="toast-close hover:bg-black/10 rounded-full p-1 transition"
            @click="showToast = false"
            aria-label="Fechar notificação"
          >
            &times;
          </button>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<style scoped>

.modal-panel {
  max-width: 44rem;
  max-height: calc(100vh - 4rem);
}
/* ── PATCH: .plan-info-column e estilos relacionados (.plan-title, .plan-subtitle,
   .plan-features, .plan-features li, .plan-feature-icon) removidos junto com a coluna ── */
.form-column {
  flex: 1;
  display: flex;
  flex-direction: column;
  background-color: var(--bg-content);
}

/* ── PATCH: agrupa logo + título à esquerda do cabeçalho ── */
.modal-title-group {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 0;
}
.modal-logo {
  width: 1.75rem;
  height: 1.75rem;
  object-fit: contain;
  flex-shrink: 0;
}

.modal-body {
  flex-grow: 1;
}

.step-content {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  height: 480px;
}
.step-title {
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-dark);
  padding-bottom: 0.5rem;
  border-bottom: 1px solid var(--border-color);
  margin-bottom: 1rem;
}
.stepper-container {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 2rem;
  padding: 0 1rem;
}
.step {
  font-size: 0.875rem;
  color: var(--text-light);
  transition: color 0.3s;
}
.step.active {
  color: var(--text-dark);
  font-weight: 600;
}
.step-divider {
  flex-grow: 1;
  height: 1px;
  background-color: var(--border-color);
  margin: 0 1rem;
}

.phone-group {
  display: flex;
  align-items: flex-end;
  gap: 0.5rem;
}
.ddi-autocomplete {
  position: relative;
  flex: 0 0 200px;
}
.ddi-autocomplete .ddi-input {
  width: 100%;
}
.ddi-options {
  position: absolute;
  top: 100%;
  left: 0;
  width: 300px;
  background-color: var(--surface);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  z-index: 10;
  list-style: none;
  padding: 0.5rem;
  margin-top: 0.5rem;
  max-height: 200px;
  overflow-y: auto;
}
.ddi-options li {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.5rem 0.75rem;
  cursor: pointer;
  border-radius: 6px;
}
.ddi-options li:hover {
  background-color: var(--bg-header);
}
.ddi-no-results {
  padding: 0.75rem;
  text-align: center;
  color: var(--text-light);
  font-style: italic;
  cursor: default;
}
.ddd-input {
  flex: 0 0 60px;
}
.phone-input {
  flex: 1;
}
.password-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}
.password-wrapper .form-input {
  padding-right: 3rem;
}
.input-error {
  border-color: var(--danger) !important;
  box-shadow: var(--focus-ring-danger) !important;
}
.password-toggle-btn {
  position: absolute;
  right: 1rem;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  color: var(--text-light);
}

.inline-error {
  margin-top: 0.5rem;
}
.inline-error-icon {
  width: 18px;
  height: 18px;
}
.terms-group {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-top: 0.5rem;
}
.terms-checkbox {
  width: 1rem;
  height: 1rem;
  border-radius: 4px;
  flex-shrink: 0;
  cursor: pointer;
}
.terms-label {
  font-size: 0.9rem;
  font-weight: 500;
  color: var(--text-dark);
}

/* ── PATCH: estilo dos botões-link dos termos ── */
.terms-link-btn {
  background: none;
  border: none;
  padding: 0;
  margin: 0;
  font-size: inherit;
  font-weight: inherit;
  font-family: inherit;
  color: var(--brand-light);
  cursor: pointer;
  text-decoration: none;
  transition: text-decoration 0.15s;
}
.terms-link-btn:hover {
  text-decoration: underline;
}
/* ── PATCH: variante que herda a cor do texto (preto) e fica em negrito ── */
.terms-link-btn--inherit {
  color: inherit;
  font-weight: 700;
  text-decoration: none;
}
.terms-link-btn--inherit:hover {
  text-decoration: none;
  opacity: 0.75;
}
/* ── FIM PATCH ─────────────────────────────── */

.whatsapp-checkbox-group {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-top: 0.75rem;
}
.whatsapp-checkbox-group .terms-label {
  font-size: 0.875rem;
  font-weight: 500;
}
.confirmation-text {
  color: var(--text-light);
  margin-bottom: 1.5rem;
  line-height: 1.6;
}
.code-inputs-container {
  display: flex;
  gap: 0.75rem;
  justify-content: center;
  margin-bottom: 1.5rem;
}
.code-input {
  width: 3.5rem;
  height: 3.5rem;
  text-align: center;
  font-size: 1.5rem;
  font-weight: 600;
  border-radius: 8px;
  border: 1px solid var(--border-color);
}
.code-input:focus {
  outline: none;
  border-color: var(--focus-border);
  box-shadow: var(--focus-ring);
}
.info-box {
  background-color: var(--bg-header);
  border-radius: 8px;
  padding: 1rem;
  font-size: 0.875rem;
  color: var(--text-light);
  text-align: center;
}

/* ── FIM PATCH ──────────────────────────────────────────────────────── */
.contact-options-row {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: nowrap;
  width: 100%;
}
.whatsapp-public-row {
  display: flex;
  align-items: center;
  width: 100%;
  margin-top: 0.1rem;
  margin-bottom: 1rem;
}
.label-row {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
}
.label-inline-error {
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--danger-text);
}
.password-hints {
  margin-top: 0.5rem;
  font-size: 0.875rem;
  color: var(--text-light);
}
.password-hints .met {
  color: var(--success);
  text-decoration: line-through;
}

.short-input {
  max-width: 300px;
}
.half-input {
  width: 50%;
  max-width: 50%;
}
.social-row {
  display: flex;
  align-items: flex-end;
  gap: 0.75rem;
  flex-wrap: nowrap;
  width: 100%;
}
.social-input {
  flex: 1 1 0;
  min-width: 0;
}
.social-input .form-label {
  font-size: 0.85rem;
}
.social-wide-input {
  width: 100%;
}
.social-wide-control {
  width: 60%;
  max-width: 60%;
}
.phone-row {
  display: flex;
  align-items: flex-end;
  gap: 0.75rem;
  width: 100%;
}
.phone-row .phone-group {
  flex: 1;
}
.phone-input-compact {
  flex: 0 0 140px;
  max-width: 160px;
}
.whatsapp-inline {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding-bottom: 0.2rem;
  flex: 0 0 auto;
}
.credentials-row {
  display: flex;
  align-items: flex-end;
  gap: 1rem;
  flex-wrap: nowrap;
  width: 100%;
}
.credentials-row .form-group {
  min-width: 0;
}

.email-summary {
  margin: -0.25rem 0 0.75rem;
  font-size: 0.85rem;
  color: var(--text-light);
}
.taken-notice {
  margin-top: 0.5rem;
  padding: 0.6rem 0.75rem;
  border-radius: 8px;
  background: #fdecec;
  border: 1px solid #f3b4b4;
  color: #7a1f1f;
  font-size: 0.8rem;
  line-height: 1.4;
}
.taken-notice p {
  margin: 0 0 0.6rem;
}
.taken-actions {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}
.taken-actions .btn {
  padding: 0.35rem 0.8rem;
  font-size: 0.8rem;
}
.claim-notice {
  margin-top: 0.5rem;
  padding: 0.6rem 0.75rem;
  border-radius: 8px;
  background: #fff8e6;
  border: 1px solid #f5d98b;
  color: #6b4e00;
  font-size: 0.8rem;
  line-height: 1.4;
}
.field-error {
  font-size: 0.8rem;
  color: var(--danger-text);
  margin-top: 0.15rem;
}

/* ── PATCH: estilos do bloco de reenvio do código (passo 4) ── */
.resend-block {
  display: flex;
  justify-content: center;
  margin-top: 1rem;
  font-size: 0.875rem;
}
.resend-btn {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  font-family: inherit;
  font-weight: 500;
  border: 1px solid var(--border-color);
  border-radius: 8px;
  background-color: var(--surface-2);
  color: var(--text-light);
  cursor: not-allowed;
  overflow: hidden;
  transition: background-color 0.2s, color 0.2s, border-color 0.2s,
    transform 0.15s;
}
.resend-btn-icon {
  width: 1rem;
  height: 1rem;
  flex-shrink: 0;
}
.resend-btn-label {
  position: relative;
  z-index: 1;
}
.resend-btn-progress {
  position: absolute;
  left: 0;
  bottom: 0;
  height: 3px;
  background-color: var(--brand);
  transition: width 1s linear;
  z-index: 0;
}
/* Estado pronto (cooldown == 0): vira clicável, ganha cor do brand, pulsa */
.resend-btn--ready {
  background-color: var(--surface);
  color: var(--text-on-brand);
  border-color: var(--brand);
  cursor: pointer;
  animation: resendPulse 1.8s ease-in-out infinite;
}
.resend-btn--ready:hover {
  background-color: var(--brand);
  color: var(--text-on-brand);
  transform: translateY(-1px);
  animation: none;
}
.resend-btn:disabled {
  opacity: 1; /* mantém visível durante cooldown */
}
@keyframes resendPulse {
  0%,
  100% {
    box-shadow: 0 0 0 0 rgba(155, 132, 255, 0.45);
  }
  50% {
    box-shadow: 0 0 0 6px rgba(155, 132, 255, 0);
  }
}
/* ── FIM PATCH ─────────────────────────────────────────────── */
</style>